import { ensureReadPermission } from '../transfer/files';
import type { LocalSource } from '../transfer/types';
import type { StoredPreparedParcel } from '../lib/storage';

export function createPreparedParcel(contactId: string, sources: LocalSource[], directories: string[]): StoredPreparedParcel {
  if (!sources.length && !directories.length) throw new Error('Choose at least one file or folder for the prepared parcel.');
  if (sources.some((source) => !source.handle)) {
    throw new Error('Prepared parcels require reusable file access. Choose files with Chrome or Edge’s file picker.');
  }
  const now = Date.now();
  return {
    id: crypto.randomUUID(),
    contactId,
    directories,
    files: sources.map((source) => ({
      fileId: source.manifest.id,
      relativePath: source.manifest.relativePath,
      size: source.manifest.size,
      lastModified: source.manifest.lastModified,
      type: source.manifest.type,
      sourceHandle: source.handle!,
    })),
    totalBytes: sources.reduce((total, source) => total + source.manifest.size, 0),
    createdAt: now,
    updatedAt: now,
  };
}

export async function restorePreparedParcel(parcel: StoredPreparedParcel): Promise<LocalSource[]> {
  const sources: LocalSource[] = [];
  for (const stored of parcel.files) {
    if (!await ensureReadPermission(stored.sourceHandle)) {
      throw new Error(`Read permission is needed again for ${stored.relativePath}.`);
    }
    const file = await stored.sourceHandle.getFile();
    if (file.size !== stored.size || file.lastModified !== stored.lastModified) {
      throw new Error(`${stored.relativePath} changed after the parcel was prepared. Remove it and prepare the current file again.`);
    }
    sources.push({
      file,
      handle: stored.sourceHandle,
      manifest: {
        id: stored.fileId,
        relativePath: stored.relativePath,
        size: stored.size,
        lastModified: stored.lastModified,
        type: stored.type,
      },
    });
  }
  return sources;
}
